/**
 * Minimaler Python-Syntax-Highlighter für die Dateivorschau (.py-Dokumente, v. a. die
 * von run_python gespeicherten script_<timestamp>.py). Kein Prism/highlight.js — gleiche
 * Philosophie wie parse/markdown.tsx: reine React-Knoten statt dangerouslySetInnerHTML,
 * damit Dokumentinhalte nie als HTML interpretiert werden.
 *
 * Deckt ab, was für eine reine Vorschau zählt: Kommentare, Strings (inkl. Triple-Quotes),
 * Keywords, Zahlen. Kein vollständiger Parser (z. B. f/r/b-String-Präfixe werden nicht
 * separat erkannt) — für die Lesbarkeit eines Skripts reicht das.
 */
import type { ReactNode } from "react";

const KEYWORDS = new Set([
  "False", "None", "True", "and", "as", "assert", "async", "await", "break",
  "class", "continue", "def", "del", "elif", "else", "except", "finally",
  "for", "from", "global", "if", "import", "in", "is", "lambda", "nonlocal",
  "not", "or", "pass", "raise", "return", "try", "while", "with", "yield",
]);

// Reihenfolge der Alternativen ist die Priorität: Kommentar > Triple-String > String > Zahl > Bezeichner.
const TOKEN_RE =
  /(#[^\n]*)|('''[\s\S]*?'''|"""[\s\S]*?""")|('(?:\\.|[^'\\\n])*'|"(?:\\.|[^"\\\n])*")|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_][A-Za-z0-9_]*\b)/g;

function highlight(src: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(src))) {
    if (match.index > lastIndex) nodes.push(src.slice(lastIndex, match.index));
    const k = key++;
    if (match[1]) nodes.push(<span className="py-cm" key={k}>{match[1]}</span>);
    else if (match[2]) nodes.push(<span className="py-str" key={k}>{match[2]}</span>);
    else if (match[3]) nodes.push(<span className="py-str" key={k}>{match[3]}</span>);
    else if (match[4]) nodes.push(<span className="py-num" key={k}>{match[4]}</span>);
    else if (match[5] && KEYWORDS.has(match[5])) nodes.push(<span className="py-kw" key={k}>{match[5]}</span>);
    else nodes.push(match[0]);
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex < src.length) nodes.push(src.slice(lastIndex));
  return nodes;
}

/** Ganzer Quelltext in einem Durchlauf (nicht zeilenweise!) — sonst reißen mehrzeilige
 * Triple-Quote-Strings auseinander. `white-space: pre` in components.css übernimmt die
 * Zeilenumbrüche aus den reinen Textknoten. */
export function PythonCode({ src }: { src: string }) {
  return (
    <pre className="py-code">
      <code>{highlight(src)}</code>
    </pre>
  );
}
